import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getAllBerita, BASE_URL } from "../services/api";
import { useLanguage, pickLang } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { getBeritaDetailPath } from "../utils/beritaSlug";
import "./Berita.css";

const Berita = () => {
  const [beritaList, setBeritaList] = useState([]);
  const [filteredBerita, setFilteredBerita] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { orgName } = useTheme();

  useEffect(() => {
    fetchBerita();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredBerita(beritaList);
    } else {
      const filtered = beritaList.filter((berita) =>
        pickLang(berita, "judul", language)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
      setFilteredBerita(filtered);
    }
  }, [searchTerm, beritaList, language]);

  const fetchBerita = async () => {
    try {
      setLoading(true);
      const response = await getAllBerita();
      if (response.data.success) {
        // Filter only berita (not welcome)
        const berita = response.data.data
          .filter((item) => item.jenisberita === "berita")
          .sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal));
        setBeritaList(berita);
        setFilteredBerita(berita);
      }
    } catch (error) {
      console.error("Error fetching berita:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div className="berita-page">
      {/* Hero Section */}
      <section className="berita-hero">
        <Container>
          <div className="text-center">
            <h1 className="hero-title">
              {language === "en" ? "News & Articles" : "Berita & Artikel"}
            </h1>
            <p className="hero-subtitle">
              {language === "en"
                ? `Latest information about halal and ${orgName} activities`
                : `Informasi terkini seputar halal dan kegiatan ${orgName}`}
            </p>
          </div>
        </Container>
      </section>

      {/* Search & Filter Section */}
      <section className="search-section py-4">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder={
                    language === "en" ? "Search news..." : "Cari berita..."
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Berita List Section */}
      <section className="berita-list-section py-5">
        <Container>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">
                {language === "en" ? "Loading news..." : "Memuat berita..."}
              </p>
            </div>
          ) : filteredBerita.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fas fa-newspaper mb-3"></i>
              <h4>
                {language === "en"
                  ? "No news found"
                  : "Tidak ada berita ditemukan"}
              </h4>
              <p className="text-muted">
                {searchTerm
                  ? `${language === "en" ? "No results for" : "Tidak ada hasil untuk"} "${searchTerm}"`
                  : language === "en"
                    ? "No news available yet"
                    : "Belum ada berita tersedia"}
              </p>
            </div>
          ) : (
            <>
              <div className="berita-count mb-4">
                <p className="text-muted">
                  {language === "en" ? "Showing" : "Menampilkan"}{" "}
                  {filteredBerita.length}{" "}
                  {language === "en" ? "news" : "berita"}
                  {searchTerm &&
                    ` ${language === "en" ? "for" : "untuk"} "${searchTerm}"`}
                </p>
              </div>
              <Row className="g-4">
                {filteredBerita.map((berita, index) => (
                  <Col md={6} lg={4} key={berita.id}>
                    <Card className="berita-card h-100">
                      {berita.foto && (
                        <div className="berita-card-image">
                          <Card.Img
                            variant="top"
                            src={`${BASE_URL}/uploads/${berita.foto}`}
                            alt={pickLang(berita, "judul", language)}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div className="berita-overlay">
                            <i className="fas fa-eye"></i>
                          </div>
                        </div>
                      )}
                      <Card.Body className="d-flex flex-column">
                        <div className="berita-date mb-2">
                          <i className="fas fa-calendar-alt me-2"></i>
                          {formatDate(berita.tanggal)}
                        </div>
                        <Card.Title className="berita-title">
                          {pickLang(berita, "judul", language)}
                        </Card.Title>
                        <Card.Text className="berita-excerpt flex-grow-1">
                          {pickLang(berita, "ringkasan", language) ||
                            stripHtml(
                              pickLang(berita, "isiberita", language),
                            ).substring(0, 150) + "..."}
                        </Card.Text>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          as={Link}
                          to={getBeritaDetailPath(
                            berita,
                            pickLang(berita, "judul", language),
                          )}
                          className="mt-auto berita-btn"
                        >
                          {language === "en"
                            ? "Read More"
                            : "Baca Selengkapnya"}{" "}
                          <i className="fas fa-arrow-right ms-1"></i>
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default Berita;
